import React from 'react';
import { Nomination, NominationStatus } from '../../types/nomination';
import './NominationCard.css';

interface NominationCardProps {
  nomination: Nomination;
  onClick?: (nomination: Nomination) => void;
}

/**
 * Get CSS class for the nomination status badge.
 */
export function getStatusBadgeClass(status: NominationStatus): string {
  switch (status) {
    case NominationStatus.DRAFT:
      return 'status-draft';
    case NominationStatus.SUBMITTED:
      return 'status-submitted';
    case NominationStatus.UNDER_REVIEW:
      return 'status-under-review';
    case NominationStatus.APPROVED:
      return 'status-approved';
    case NominationStatus.REJECTED:
      return 'status-rejected';
    case NominationStatus.PUBLISHED:
      return 'status-published';
    default:
      return '';
  }
}

/**
 * Format an ISO date string for display.
 */
export function formatNominationDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Strip trailing JSON metadata from nomination reason text.
 * Some nominations have raw JSON appended (taskDescription, problemType, etc.)
 */
export function cleanReason(reason: string): string {
  if (!reason) return '';
  // Approach 1: Match known JSON metadata keys
  const jsonPatterns = [
    '{"taskDescription"',
    '{"problemType"',
    '{"measurableImpact"',
    '{"beyondNormalResponsibility"',
    '{"clearOwnership"',
    '{"clientAppreciation"',
  ];
  let cutIndex = reason.length;
  for (const pattern of jsonPatterns) {
    const idx = reason.indexOf(pattern);
    if (idx !== -1 && idx < cutIndex) {
      cutIndex = idx;
    }
  }
  // Approach 2: Generic fallback — find any {"key": that looks like appended JSON
  const jsonRegex = /\{"[a-zA-Z]+"\s*:/;
  const match = jsonRegex.exec(reason);
  if (match && match.index < cutIndex) {
    // Verify it's actually valid-ish JSON by checking if there's a closing }
    const remaining = reason.substring(match.index);
    if (remaining.includes('}')) {
      cutIndex = match.index;
    }
  }
  return reason.substring(0, cutIndex).trim();
}

/**
 * NominationCard Component
 * Displays nominee name, award type, reason, status badge, and dates.
 * Requirements: 5.2, 7.3
 */
const NominationCard: React.FC<NominationCardProps> = ({ nomination, onClick }) => {
  const handleClick = () => {
    if (onClick) {
      onClick(nomination);
    }
  };

  return (
    <div
      className="nomination-card"
      onClick={handleClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); } : undefined}
    >
      <div className="nomination-card-header">
        <h3 className="nomination-card-title">{nomination.nomineeName}</h3>
        <span className={`nomination-status-badge ${getStatusBadgeClass(nomination.status)}`}>
          {nomination.status}
        </span>
      </div>

      <span className="nomination-award-type">{nomination.awardType}</span>

      <p className="nomination-card-reason">{cleanReason(nomination.reason)}</p>

      <div className="nomination-card-meta">
        <span className="nomination-card-nominator">
          Nominated by: {nomination.nominatorName}
        </span>
        <span>Created: {formatNominationDate(nomination.createdAt)}</span>
        {nomination.publishedAt && (
          <span>Published: {formatNominationDate(nomination.publishedAt)}</span>
        )}
      </div>
    </div>
  );
};

export default NominationCard;
